/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.form = this.querySelector('product-form');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      this.cleanupQuickAddZoom();
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Cleans up zoom-related event listeners
     */
    cleanupQuickAddZoom() {
      if (this.quickAddZoomLinks) {
        this.quickAddZoomLinks.forEach((zoomLink) => {
          if (zoomLink._zoomHandlers) {
            zoomLink.removeEventListener('mouseenter', zoomLink._zoomHandlers.mouseenter);
            zoomLink.removeEventListener('mousemove', zoomLink._zoomHandlers.mousemove);
            zoomLink.removeEventListener('mouseleave', zoomLink._zoomHandlers.mouseleave);
            delete zoomLink._zoomHandlers;
          }
          zoomLink._activeZoomImage = null;
        });
      }
    }

    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl)
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    handleOpenCartClick(evt) {
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        this.updateMedia(evt.detail.variant.featured_media.id);
      } else {
        this.updateMedia();
      }
    }

    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      // Clear cached lifestyle data for new product
      this.cachedLifestyleData = null;

      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl)
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.querySelector('.cc-main-product .js-product');
        this.renderProduct(opener);
      }

      this.fetch = null;
      opener.removeAttribute('aria-disabled');
    }

    close() {
      this.cleanupQuickAddZoom();
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    renderProduct(opener) {
      this.cleanupQuickAddZoom();

      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickadd');

      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
        variantPicker.dataset.showOutofstock = 'false';
        this.selectFirstVariant = variantPicker.dataset.selectFirstVariant === 'true';
      }

      const sizeChartModal = this.productEl.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }

      this.updateContent();
      this.updateForm();

      document.querySelectorAll(".add_tocart_custom .custom_add_cart").forEach((element) => {
        element.addEventListener("click", () => {
          const button = document.querySelector('.drawer__footer product-form .product-info__add-button button[type="submit"]');
          if (button) button.click();
        });
      });

      const activeMedia = this.productEl.querySelector('.media-viewer__item.is-current-variant');
      if (activeMedia) this.updateMedia(activeMedia.dataset.mediaId);

      if (opener.dataset.selectedColor && this.selectFirstVariant) {
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
    }

    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        colorOptionBox.click();
      } else {
        const colorOptionDropdown = this.querySelector(`.custom-select__option[data-value="${opener.dataset.selectedColor}"]`);
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    // MEDIA UPDATE LOGIC
    updateMedia(mediaId) {
      const container = this.querySelector(".quick-add-info__media");
      if (!container) return;

      const width = container.offsetWidth;
      const currentVariantOption = this.getCurrentVariantOption(mediaId);

      if (currentVariantOption) {
        const variantImages = this.getImagesForVariantOption(currentVariantOption);
        if (variantImages.length > 0) {
          this.buildSliderFromImages(variantImages, width);
          return;
        }
      }

      this.buildSliderFromAllImages(mediaId, width);
    }

    getCurrentVariantOption(mediaId) {
      if (!mediaId) return null;

      const variantMediaDataEl = this.productEl.querySelector('.js-data-variant-media');
      if (!variantMediaDataEl) return null;

      const variantMediaData = JSON.parse(variantMediaDataEl.textContent);
      const targetMedia = variantMediaData.variantMedia.find(vm => vm.mediaId.toString() === mediaId.toString());
      return targetMedia ? targetMedia.title : null;
    }

    getImagesForVariantOption(targetOption) {
      const variantMediaDataEl = this.productEl.querySelector('.js-data-variant-media');
      if (!variantMediaDataEl) return [];

      const variantMediaData = JSON.parse(variantMediaDataEl.textContent);
      const mediaGroupMap = { groups: {} };
      const viewerItems = this.productEl.querySelectorAll('.media-viewer__item, [data-media-id]');
      let currentMediaOptionName = false;
      viewerItems.forEach((item) => {
        for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
          if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
            if (currentMediaOptionName !== variantMediaData.variantMedia[i].title) {
              currentMediaOptionName = variantMediaData.variantMedia[i].title;
            }
            break;
          }
        }

        if (currentMediaOptionName) {
          if (!mediaGroupMap.groups[currentMediaOptionName]) {
            mediaGroupMap.groups[currentMediaOptionName] = {
              name: currentMediaOptionName,
              items: [],
            };
          }

          const img = item.querySelector('img[src], img[data-src]');
          if (img) {
            mediaGroupMap.groups[currentMediaOptionName].items.push({
              main: item,
              img: img,
              mediaId: item.dataset.mediaId
            });
          }
        }
      });

      const targetGroup = mediaGroupMap.groups[targetOption];
      return targetGroup ? targetGroup.items : [];
    }

    // LIFESTYLE CONTENT LOGIC
    getLifestyleMetafields() {
      if (this.cachedLifestyleData) {
        return this.cachedLifestyleData;
      }

      this.cachedLifestyleData = {
        lifestyle_image: null,
        lifestyle_video: null
      };

      try {
        const productScripts = this.productEl.querySelectorAll('script');

        for (let script of productScripts) {
          if (script.textContent.includes('"product":') && script.textContent.includes('metafields')) {
            try {
              const jsonData = JSON.parse(script.textContent);

              if (jsonData.metafields && Object.keys(jsonData.metafields).length > 0) {
                const firstVariantId = Object.keys(jsonData.metafields)[0];
                const metafields = jsonData.metafields[firstVariantId];

                if (metafields && metafields.custom) {
                  if (metafields.custom.lifestyle_image) {
                    this.cachedLifestyleData.lifestyle_image = metafields.custom.lifestyle_image;
                  }
                  if (metafields.custom.lifestyle_video) {
                    this.cachedLifestyleData.lifestyle_video = metafields.custom.lifestyle_video;
                  }
                  break;
                }
              }
            } catch (e) {
              continue;
            }
          }
        }
      } catch (e) {
        console.warn('Error getting lifestyle metafields:', e);
      }

      return this.cachedLifestyleData;
    }

    getExistingLifestyleContent() {
      const galleryViewer = this.productEl.querySelector('#gallery-viewer');
      if (!galleryViewer) return { image: null, video: null };

      const galleryItems = galleryViewer.querySelectorAll('li');
      let lifestyleImage = null;
      let lifestyleVideo = null;

      galleryItems.forEach((item) => {
        if (!item.dataset.mediaId) {
          const hasImage = !!item.querySelector('img');
          const hasVimeo = !!item.querySelector('iframe[src*="vimeo"]');

          if (hasImage && !lifestyleImage) {
            lifestyleImage = item;
          } else if (hasVimeo && !lifestyleVideo) {
            lifestyleVideo = item;
          }
        }
      });

      return { image: lifestyleImage, video: lifestyleVideo };
    }

    createLifestyleImageHTML(imageSrc, width) {
      const containerHeight = Math.round(width * 1.3);
      const zoomWidth = width * 6;

      let cleanSrc = imageSrc;
      if (cleanSrc.startsWith('//')) {
        cleanSrc = 'https:' + cleanSrc;
      }
      cleanSrc = cleanSrc.split('?')[0];

      const mainImageUrl = `${cleanSrc}?width=${width}`;
      const mainImageSrcset = `${cleanSrc}?width=${width}, ${cleanSrc}?width=${width * 2} 2x`;
      const zoomImageUrl = `${cleanSrc}?width=${zoomWidth}`;

      return `
        <div class="media js-zoom-link" style="position: relative; width: 100%; height: ${containerHeight}px; overflow: hidden; cursor: crosshair; display: block; background-color: #f8f9fa;">
          <img
            src="${mainImageUrl}"
            srcset="${mainImageSrcset}"
            width="${width}"
            height="${containerHeight}"
            alt="Lifestyle"
            class="product-image"
            loading="lazy"
            style="display: block; width: 100%; height: 100%; object-fit: cover; object-position: center; position: relative; z-index: 1;">
          <img
            src="${zoomImageUrl}"
            alt="Lifestyle"
            class="zoom-image js-zoom-image"
            loading="lazy"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; opacity: 0; pointer-events: none; z-index: 2; transform: scale(1); transform-origin: center center; transition: opacity 0.3s ease-in-out;">
        </div>
      `;
    }

    createLifestyleVideoHTML(vimeoId, width) {
      const containerHeight = Math.round(width * 1.3);

      return `
        <div class="media relative" style="position: relative; width: 100%; height: ${containerHeight}px; background-color: #f8f9fa;">
          <iframe
            title="vimeo-player"
            src="https://player.vimeo.com/video/${vimeoId}?title=0&muted=1&autoplay=1&autopause=0&loop=1&background=1&app_id="
            width="100%"
            height="100%"
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; transform: scale(1.2);">
          </iframe>
        </div>
      `;
    }

    // SLIDER BUILDERS
    buildSliderFromImages(images, width) {
      const container = this.querySelector(".quick-add-info__media");
      const lifestyleMetafields = this.getLifestyleMetafields();
      const existingLifestyle = this.getExistingLifestyleContent();

      let sliderHTML = "";

      // Position 1: First variant image
      if (images.length > 0) {
        const firstImage = images[0];
        const img = firstImage.img;
        const src = (img.src || img.dataset.src || '').split("&width=")[0];
        const aspectRatio = img.naturalWidth && img.naturalHeight ?
          img.naturalWidth / img.naturalHeight : img.width / img.height || 1;

        const imageHTML = this.createZoomImageHTML(src, width, aspectRatio, img.alt || '');
        sliderHTML += `<swiper-slide class="flex items-center justify-center h-full" data-media-id="${firstImage.mediaId}" data-slide-type="variant">${imageHTML}</swiper-slide>`;
      }

      // Position 2: Lifestyle image
      let lifestyleImageSrc = null;
      if (existingLifestyle.image) {
        const existingImg = existingLifestyle.image.querySelector('img');
        if (existingImg) {
          lifestyleImageSrc = existingImg.src.split('?')[0];
        }
      } else if (lifestyleMetafields.lifestyle_image?.value) {
        lifestyleImageSrc = lifestyleMetafields.lifestyle_image.value;
      }

      if (lifestyleImageSrc) {
        const lifestyleImageHTML = this.createLifestyleImageHTML(lifestyleImageSrc, width);
        sliderHTML += `<swiper-slide class="flex items-center justify-center h-full" data-slide-type="lifestyle-image">${lifestyleImageHTML}</swiper-slide>`;
      }

      // Position 3: Lifestyle video
      let vimeoId = null;
      if (existingLifestyle.video) {
        const existingIframe = existingLifestyle.video.querySelector('iframe[src*="vimeo"]');
        if (existingIframe) {
          const vimeoMatch = existingIframe.src.match(/video\/(\d+)/);
          if (vimeoMatch) {
            vimeoId = vimeoMatch[1];
          }
        }
      } else if (lifestyleMetafields.lifestyle_video) {
        vimeoId = lifestyleMetafields.lifestyle_video;
      }

      if (vimeoId) {
        const lifestyleVideoHTML = this.createLifestyleVideoHTML(vimeoId, width);
        sliderHTML += `<swiper-slide class="flex items-center justify-center h-full" data-slide-type="lifestyle-video">${lifestyleVideoHTML}</swiper-slide>`;
      }

      // Position 4+: Rest of variant images
      if (images.length > 1) {
        images.slice(1).forEach((imageData) => {
          if (!imageData.mediaId || imageData.mediaId === 'undefined') return;

          const img = imageData.img;
          const src = (img.src || img.dataset.src || '').split("&width=")[0];
          const aspectRatio = img.naturalWidth && img.naturalHeight ?
            img.naturalWidth / img.naturalHeight : img.width / img.height || 1;

          const imageHTML = this.createZoomImageHTML(src, width, aspectRatio, img.alt || '');
          sliderHTML += `<swiper-slide class="flex items-center justify-center h-full" data-media-id="${imageData.mediaId}" data-slide-type="variant">${imageHTML}</swiper-slide>`;
        });
      }

      this.renderSlider(container, sliderHTML);
    }

    buildSliderFromAllImages(mediaId, width) {
      const container = this.querySelector(".quick-add-info__media");
      const img = this.productEl.querySelector(`#gallery-viewer`);

      if (!img) {
        container.innerHTML = '<div class="text-center p-8 text-gray-500">No images available</div>';
        return;
      }

      const imageLI = img.querySelectorAll("li");
      const lifestyleMetafields = this.getLifestyleMetafields();
      const existingLifestyle = this.getExistingLifestyleContent();
      const variantImages = Array.from(imageLI).filter(li => li.dataset.mediaId);

      // Move featured image to front if specified
      if (mediaId) {
        const featuredIndex = variantImages.findIndex(li => {
          const imgEl = li.querySelector("img");
          return li.dataset.mediaId === mediaId ||
                 imgEl?.src?.includes(mediaId) ||
                 imgEl?.dataset.src?.includes(mediaId);
        });

        if (featuredIndex > 0) {
          const featuredImage = variantImages[featuredIndex];
          variantImages.splice(featuredIndex, 1);
          variantImages.unshift(featuredImage);
        }
      }

      let sliderHTML = "";

      // Position 1: First variant image
      if (variantImages.length > 0) {
        const firstVariant = variantImages[0];
        const imgEl = firstVariant.querySelector("img");
        if (imgEl) {
          const src = imgEl.src ? imgEl.src.split("&width=")[0] : imgEl.dataset.src?.split("&width=")[0];
          if (src) {
            const aspectRatio = imgEl.width && imgEl.height ? imgEl.width / imgEl.height : 1;
            const imageHTML = this.createZoomImageHTML(src, width, aspectRatio, imgEl.alt || '');
            sliderHTML += `<swiper-slide class="flex items-center justify-center h-full" data-media-id="${firstVariant.dataset.mediaId}" data-slide-type="variant">${imageHTML}</swiper-slide>`;
          }
        }
      }

      // Position 2: Lifestyle image
      let lifestyleImageSrc = null;
      if (existingLifestyle.image) {
        const existingImg = existingLifestyle.image.querySelector('img');
        if (existingImg) {
          lifestyleImageSrc = existingImg.src.split('&width=')[0].split('?')[0];
        }
      } else if (lifestyleMetafields.lifestyle_image?.value) {
        lifestyleImageSrc = lifestyleMetafields.lifestyle_image.value;
      }

      if (lifestyleImageSrc) {
        const lifestyleImageHTML = this.createLifestyleImageHTML(lifestyleImageSrc, width);
        sliderHTML += `<swiper-slide class="flex items-center justify-center h-full" data-slide-type="lifestyle-image">${lifestyleImageHTML}</swiper-slide>`;
      }

      // Position 3: Lifestyle video
      let vimeoId = null;
      if (existingLifestyle.video) {
        const existingIframe = existingLifestyle.video.querySelector('iframe[src*="vimeo"]');
        if (existingIframe) {
          const vimeoMatch = existingIframe.src.match(/video\/(\d+)/);
          if (vimeoMatch) {
            vimeoId = vimeoMatch[1];
          }
        }
      } else if (lifestyleMetafields.lifestyle_video) {
        vimeoId = lifestyleMetafields.lifestyle_video;
      }

      if (vimeoId) {
        const lifestyleVideoHTML = this.createLifestyleVideoHTML(vimeoId, width);
        sliderHTML += `<swiper-slide class="flex items-center justify-center h-full" data-slide-type="lifestyle-video">${lifestyleVideoHTML}</swiper-slide>`;
      }

      // Position 4+: Rest of variant images
      if (variantImages.length > 1) {
        variantImages.slice(1).forEach((elm) => {
          const imgEl = elm.querySelector("img");
          if (!imgEl) return;

          const src = imgEl.src ? imgEl.src.split("&width=")[0] : imgEl.dataset.src?.split("&width=")[0];
          if (!src) return;

          const aspectRatio = imgEl.width && imgEl.height ? imgEl.width / imgEl.height : 1;
          const imageHTML = this.createZoomImageHTML(src, width, aspectRatio, imgEl.alt || '');
          sliderHTML += `<swiper-slide class="flex items-center justify-center h-full" data-media-id="${elm.dataset.mediaId}" data-slide-type="variant">${imageHTML}</swiper-slide>`;
        });
      }

      this.renderSlider(container, sliderHTML);
    }

    renderSlider(container, sliderHTML) {
      container.innerHTML = `
        <swiper-container class="dooney-swiper-container h-full" id="swiper-product-quick-drawer-slider" slides-per-view="1" css-mode="true" loop="true" keyboard="true" pagination="true" pagination-clickable="true" free-mode="true" a11y="false" navigation-prev-el=".quick-add-info__media .dooney-prev" navigation-next-el=".quick-add-info__media .dooney-next" scrollbar-draggable="false" scrollbar-enabled="false" scrollbar-snap-on-release="false" scrollbar-hide="true">
          ${sliderHTML}
        </swiper-container>
        <button aria-label="Previous Icon" class="dooney-prev absolute left-0 top-1/2 transform -translate-y-1/2 z-10 flex items-center justify-center">
          <svg width="11" height="14" viewBox="0 0 11 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.38296 20.0762C0.111788 19.805 0.111788 19.3654 0.38296 19.0942L9.19758 10.2796L0.38296 1.46497C0.111788 1.19379 0.111788 0.754138 0.38296 0.482966C0.654131 0.211794 1.09379 0.211794 1.36496 0.482966L10.4341 9.55214C10.8359 9.9539 10.8359 10.6053 10.4341 11.007L1.36496 20.0762C1.09379 20.3474 0.654131 20.3474 0.38296 20.0762Z" fill="currentColor" transform-origin="center" transform="rotate(180)"></path>
          </svg>
        </button>
        <button aria-label="Next Icon" class="dooney-next absolute right-0 top-1/2 transform -translate-y-1/2 z-10 flex items-center justify-center">
          <svg width="11" height="14" viewBox="0 0 11 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.38296 20.0762C0.111788 19.805 0.111788 19.3654 0.38296 19.0942L9.19758 10.2796L0.38296 1.46497C0.111788 1.19379 0.111788 0.754138 0.38296 0.482966C0.654131 0.211794 1.09379 0.211794 1.36496 0.482966L10.4341 9.55214C10.8359 9.9539 10.8359 10.6053 10.4341 11.007L1.36496 20.0762C1.09379 20.3474 0.654131 20.3474 0.38296 20.0762Z" fill="currentColor"></path>
          </svg>
        </button>
        <div class="loading-spinner loading-spinner--out" role="status" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 20;">
          <span class="sr-only">Loading...</span>
        </div>
      `;

      setTimeout(() => {
        this.initQuickAddZoom();
      }, 100);
    }

    createZoomImageHTML(src, width, aspectRatio, alt) {
      const zoomWidth = width * 6;
      const containerHeight = Math.round(width * 1.3);
      return `
        <div class="media js-zoom-link" style="position: relative; width: 100%; height: ${containerHeight}px; overflow: hidden; cursor: crosshair; display: block; background-color: #f8f9fa;">
          <img
            src="${src}&width=${width}"
            srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x"
            width="${width}"
            height="${containerHeight}"
            alt="${alt}"
            loading="lazy"
            class="product-image"
            style="display: block; width: 100%; height: 100%; object-fit: contain; object-position: center; position: relative; z-index: 1;">
          <img
            src="${src}&width=${zoomWidth}"
            alt="${alt}"
            class="zoom-image js-zoom-image"
            loading="lazy"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; opacity: 0; pointer-events: none; z-index: 2; transform: scale(1); transform-origin: center center; transition: opacity 0.3s ease-in-out;">
        </div>
      `;
    }

    // ZOOM FUNCTIONALITY
    initQuickAddZoom() {
      this.quickAddZoomLinks = this.querySelectorAll('.quick-add-info__media .js-zoom-link');
      this.quickAddLoadingSpinner = this.querySelector('.quick-add-info__media .loading-spinner');

      if (!this.quickAddZoomLinks.length) return;

      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      if (!isDesktop) return;

      this.quickAddZoomLinks.forEach((zoomLink) => {
        const mouseEnterHandler = (evt) => this.handleQuickAddZoomMouseEnter(evt);
        const mouseMoveHandler = (evt) => this.handleQuickAddZoomMouseMove(evt);
        const mouseLeaveHandler = (evt) => this.handleQuickAddZoomMouseLeave(evt);

        zoomLink._zoomHandlers = {
          mouseenter: mouseEnterHandler,
          mousemove: mouseMoveHandler,
          mouseleave: mouseLeaveHandler
        };

        zoomLink.addEventListener('mouseenter', mouseEnterHandler);
        zoomLink.addEventListener('mousemove', mouseMoveHandler);
        zoomLink.addEventListener('mouseleave', mouseLeaveHandler);
      });
    }

    handleQuickAddZoomMouseEnter(evt) {
      const zoomLink = evt.currentTarget;
      const zoomImage = zoomLink.querySelector('.js-zoom-image');

      if (!zoomImage) return;

      if (this.quickAddLoadingSpinner) {
        this.quickAddLoadingSpinner.classList.remove('loading-spinner--out');
      }

      const activateZoom = () => {
        zoomImage.style.opacity = '1';
        zoomImage.style.transform = 'scale(3)';
        zoomImage.classList.add('zoom-active');

        if (this.quickAddLoadingSpinner) {
          this.quickAddLoadingSpinner.classList.add('loading-spinner--out');
        }
      };

      if (zoomImage.complete && zoomImage.naturalHeight !== 0) {
        activateZoom();
      } else {
        const handleLoad = () => {
          activateZoom();
          zoomImage.removeEventListener('load', handleLoad);
          zoomImage.removeEventListener('error', handleError);
        };

        const handleError = () => {
          if (this.quickAddLoadingSpinner) {
            this.quickAddLoadingSpinner.classList.add('loading-spinner--out');
          }
          zoomImage.removeEventListener('load', handleLoad);
          zoomImage.removeEventListener('error', handleError);
        };

        zoomImage.addEventListener('load', handleLoad);
        zoomImage.addEventListener('error', handleError);
      }

      zoomLink._activeZoomImage = zoomImage;
    }

    handleQuickAddZoomMouseMove(evt) {
      const zoomLink = evt.currentTarget;
      const zoomImage = zoomLink._activeZoomImage || zoomLink.querySelector('.js-zoom-image');

      if (!zoomImage) return;

      if (!zoomImage.classList.contains('zoom-active') && zoomImage.style.opacity === '1') {
        zoomImage.classList.add('zoom-active');
      }

      if (!zoomImage.classList.contains('zoom-active')) return;

      const rect = zoomLink.getBoundingClientRect();
      const offsetX = evt.clientX - rect.left;
      const offsetY = evt.clientY - rect.top;

      const x = Math.max(0, Math.min(100, (offsetX / rect.width) * 100));
      const y = Math.max(0, Math.min(100, (offsetY / rect.height) * 100));

      zoomImage.style.transformOrigin = `${x}% ${y}%`;
      zoomImage.style.transform = 'scale(2.5)';
    }

    handleQuickAddZoomMouseLeave(evt) {
      const zoomLink = evt.currentTarget;
      const zoomImage = zoomLink._activeZoomImage || zoomLink.querySelector('.js-zoom-image');

      if (zoomImage) {
        zoomImage.style.transform = 'scale(1)';
        zoomImage.style.transformOrigin = 'center center';

        setTimeout(() => {
          zoomImage.classList.remove('zoom-active');
        }, 300);
      }

      zoomLink._activeZoomImage = null;
    }

    // CONTENT AND FORM UPDATES
    updateContent() {
      let weightElem = this.getElementHtml(".product-info__weight");
      if (weightElem && weightElem.length > 0) {
        weightElem = `<div class="product-info__weight text-sm mt-2">${weightElem}</div>`;
      }

      const updateOptions = this.getElementHtml(".product-options", ".custom-option:not(.quick-add-hidden)");

      this.content.innerHTML = `
        <div class="quick-add-info grid">
          <div class="quick-add-info__media${theme.settings.blendProductImages ? " image-blend" : ""}"></div>
          <div class="quick-add-info__details">
            <div class="product-title">
              <h1 class="product-title__text text-h3 mb-2">
                  ${this.getElementHtml('.product-title')}
              </h1>
            </div>
            ${(updateOptions != '') ? '<hr>': '<hr class="white-bg">'}
            <div class="product-options ${(updateOptions != '') ? '' : 'no-options'}">
              <div class="product-vendor-sku mb-2 text-sm">
                ${this.getElementHtml(".product-vendor-sku")}
              </div>
              ${this.getElementHtml(".product-options", ".custom-option:not(.quick-add-hidden)")}
            </div>
            ${weightElem}
            <hr>
            <div class="product-price">
              ${this.getElementHtml(".product-price")}
            </div>
            <hr>
            <div class="product-backorder hidden">
              ${this.getElementHtml('.product-backorder')}
            </div>
            <div class="add_tocart_custom">
              <div class="product-info__add-button">
                <button class="btn btn--primary w-full custom_add_cart flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M12 2c2.761 0 5 2.089 5 4.667V8h2.2a.79.79 0 0 1 .8.778v12.444a.79.79 0 0 1-.8.778H4.8a.789.789 0 0 1-.8-.778V8.778A.79.79 0 0 1 4.8 8H7V6.667C7 4.09 9.239 2 12 2zm6.4 7.556H5.6v10.888h12.8V9.556zm-6.4-6c-1.84 0-3.333 1.392-3.333 3.11V8h6.666V6.667c0-1.719-1.492-3.111-3.333-3.111z"/></svg> Add to cart
                </button>
              </div>
            </div>
            <div class="text-theme-light text-sm mt-4 text-center">
              <a href="${this.productUrl}" class="link js-prod-link view_details_link">
                ${theme.strings.viewDetails}
              </a>
            </div>
          </div>
          <div class="quick-add-info__details md:hidden"></div>
        </div>
      `;

      this.classList.remove("is-loading");
      this.content.classList.remove("drawer__content--out");
    }

    updateForm() {
      const productForm = this.productEl.querySelector('product-form');
      this.footer.classList.remove('quick-add__footer-message');

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        const signUpForm = this.productEl.querySelector('.product-signup');
        if (signUpForm) {
          this.form.innerHTML = signUpForm.innerHTML;
        } else {
          this.footer.classList.add('quick-add__footer-message');
          this.form.innerHTML = `
            <div class="alert quick-add__alert bg-info-bg text-info-text">
              <div class="flex">
                <div>
                  <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                    <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                    <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                  <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
                </div>
              </div>
            </div>`;
        }
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    getElementHtml(selector, ...additionalSelectors) {
      const selectors = [selector].concat(additionalSelectors);
      let html = '';
      selectors.forEach((sel) => {
        const els = this.productEl.querySelectorAll(sel);
        Array.from(els).forEach((el) => {
          html += el.innerHTML;
        });
      });
      return html;
    }

    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}